import { Components, Theme } from '@mui/material/styles';

export const getMuiButton = (
  app: 'kc' | 'rti',
): Components<Theme>['MuiButton'] => ({
  defaultProps: {
    disableElevation: true,
  },
  variants: [
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        height: '34px',
        padding: '0px 10px',
        ...theme.typography.body2,
      }),
    },
    {
      props: { size: 'medium' },
      style: {
        height: '44px',
        padding: '10px 20px',
      },
    },
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        height: '52px',
        padding: '10px 24px',
        ...theme.typography.bodyLarge,
      }),
    },
    {
      // Default contained color: this rule matches when `color` is omitted,
      // since MUI resolves an unpassed `color` to `primary`. kc keeps MUI's
      // primary; rti's default contained button uses the secondary palette and
      // carries the brand shadow — it's the same brand CTA as contained
      // secondary below.
      props: { variant: 'contained', color: 'primary' },
      style: ({ theme }) =>
        app === 'rti'
          ? {
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              boxShadow: theme.customShadows.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                boxShadow: theme.customShadows.main,
              },
              '&:active': { boxShadow: theme.customShadows.main },
              '&.Mui-disabled': { boxShadow: 'none' },
            }
          : {},
    },
    {
      // rti's contained secondary button is the same brand CTA — carries the
      // brand shadow. Scoped to `contained` so outlined/text secondary buttons
      // stay flat.
      props: { variant: 'contained', color: 'secondary' },
      style: ({ theme }) =>
        app === 'rti'
          ? {
              boxShadow: theme.customShadows.main,
              '&:hover': { boxShadow: theme.customShadows.main },
              '&:active': { boxShadow: theme.customShadows.main },
              '&.Mui-disabled': { boxShadow: 'none' },
            }
          : {},
    },
  ],
  styleOverrides: {
    root: {
      borderRadius: '4px',
      minWidth: '32px',
    },
    // .MuiButton-outlinedPrimary slot (single-class specificity): overrides
    // MUI's default outlined border but still loses to component-level `sx`,
    // so call sites can override the border/color (e.g. white on dashboard).
    outlinedPrimary: ({ theme }) => ({
      borderColor: theme.palette.customColors.secondaryBtnBorder,
      color: theme.palette.customColors.outlinedBtnText,
      '&:hover': {
        borderColor: theme.palette.primary.light,
        color:
          app === 'rti'
            ? theme.palette.primary.light
            : theme.palette.text.primary,
        backgroundColor: 'transparent',
      },
    }),
  },
});

export default getMuiButton;
